/**
 * Grid snapping utilities for shop floor layout alignment
 */

export const GRID_SIZES = [10, 20, 25] as const;
export type GridSize = (typeof GRID_SIZES)[number];

/**
 * Snap a single value to the nearest grid point
 */
export function snapToGrid(value: number, gridSize: number = 20): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Snap a position (x, y) to the nearest grid point
 */
export function snapPosition(
  pos: { x: number; y: number },
  gridSize: number = 20
): { x: number; y: number } {
  return {
    x: snapToGrid(pos.x, gridSize),
    y: snapToGrid(pos.y, gridSize),
  };
}

/**
 * Snap dimensions (width, height) to grid with minimum size enforcement
 */
export function snapDimensions(
  dim: { width: number; height: number },
  gridSize: number = 20,
  minWidth: number = 100,
  minHeight: number = 100
): { width: number; height: number } {
  return {
    width: Math.max(minWidth, snapToGrid(dim.width, gridSize)),
    height: Math.max(minHeight, snapToGrid(dim.height, gridSize)),
  };
}

/**
 * Alignment guide interface
 */
export interface AlignmentGuide {
  type: 'vertical' | 'horizontal';
  position: number; // x for vertical, y for horizontal
  start: number;
  end: number;
}

/**
 * Find alignment guides by detecting when bay edges are within threshold of other bay edges
 */
export function findAlignmentGuides(
  currentBay: { x: number; y: number; width: number; height: number },
  otherBays: Array<{ x: number; y: number; width: number; height: number }>,
  threshold: number = 5
): AlignmentGuide[] {
  const guides: AlignmentGuide[] = [];
  
  const currentEdges = {
    left: currentBay.x,
    right: currentBay.x + currentBay.width,
    top: currentBay.y,
    bottom: currentBay.y + currentBay.height,
    centerX: currentBay.x + currentBay.width / 2,
    centerY: currentBay.y + currentBay.height / 2,
  };

  for (const other of otherBays) {
    const otherEdges = {
      left: other.x,
      right: other.x + other.width,
      top: other.y,
      bottom: other.y + other.height,
      centerX: other.x + other.width / 2,
      centerY: other.y + other.height / 2,
    };

    // Check vertical alignments (left-to-left, left-to-right, right-to-left, right-to-right, center-to-center)
    const verticalChecks: Array<[number, number]> = [
      [currentEdges.left, otherEdges.left],
      [currentEdges.left, otherEdges.right],
      [currentEdges.right, otherEdges.left],
      [currentEdges.right, otherEdges.right],
      [currentEdges.centerX, otherEdges.centerX],
    ];

    for (const [current, otherVal] of verticalChecks) {
      if (Math.abs(current - otherVal) <= threshold) {
        guides.push({
          type: 'vertical',
          position: otherVal,
          start: Math.min(currentBay.y, other.y),
          end: Math.max(currentBay.y + currentBay.height, other.y + other.height),
        });
      }
    }

    // Check horizontal alignments (top-to-top, top-to-bottom, bottom-to-top, bottom-to-bottom, center-to-center)
    const horizontalChecks: Array<[number, number]> = [
      [currentEdges.top, otherEdges.top],
      [currentEdges.top, otherEdges.bottom],
      [currentEdges.bottom, otherEdges.top],
      [currentEdges.bottom, otherEdges.bottom],
      [currentEdges.centerY, otherEdges.centerY],
    ];

    for (const [current, otherVal] of horizontalChecks) {
      if (Math.abs(current - otherVal) <= threshold) {
        guides.push({
          type: 'horizontal',
          position: otherVal,
          start: Math.min(currentBay.x, other.x),
          end: Math.max(currentBay.x + currentBay.width, other.x + other.width),
        });
      }
    }
  }

  // Deduplicate guides by position
  const uniqueGuides: AlignmentGuide[] = [];
  const seen = new Set<string>();
  
  for (const guide of guides) {
    const key = `${guide.type}-${guide.position}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueGuides.push(guide);
    }
  }

  return uniqueGuides;
}

/**
 * Snap to alignment guide if within threshold, otherwise snap to grid
 */
export function snapWithAlignment(
  value: number,
  alignmentValue: number | null,
  gridSize: number,
  threshold: number = 5
): number {
  if (alignmentValue !== null && Math.abs(value - alignmentValue) <= threshold) {
    return alignmentValue;
  }
  return snapToGrid(value, gridSize);
}
